import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./OrgStoresMap.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AHIcon, AHText, useLayoutContext, useTheme } from "arkh";
import { config } from "@/src/config";
import { graphql, useFragment } from "react-relay";
import { OrgStoresMap_stores$key } from "./__generated__/OrgStoresMap_stores.graphql";
import { useIntl } from "react-intl";
import { StyleSheet, View } from "react-native";

mapboxgl.accessToken = config.mapboxToken;

const MARKER_SIZE = 16;
const FIT_PADDING = 60;
const MIN_MAP_HEIGHT = 300;
const DEFAULT_CENTER: [number, number] = [18.07, 59.33];
const DEFAULT_ZOOM = 4;

function createMarkerElement(color: string, animated: boolean): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "store-marker";
  el.style.width = `${MARKER_SIZE}px`;
  el.style.height = `${MARKER_SIZE}px`;
  el.style.borderRadius = "50%";
  el.style.backgroundColor = color;
  if (animated) {
    el.style.setProperty("--marker-color-faded", `${color}66`);
    el.style.setProperty("--marker-color-transparent", `${color}00`);
    const delay = Math.random() * 2;
    el.style.animation = `store-marker-pulse 2s ease-in-out ${delay}s infinite`;
  }
  return el;
}

function computeBounds(
  stores: readonly { latitude: number | null; longitude: number | null }[]
): mapboxgl.LngLatBounds | null {
  const bounds = new mapboxgl.LngLatBounds();
  let count = 0;
  for (const store of stores) {
    if (store.latitude == null || store.longitude == null) continue;
    bounds.extend([store.longitude, store.latitude]);
    count++;
  }
  return count > 0 ? bounds : null;
}

type PopupContent = {
  name: string;
  statusLabel: string;
};

function StorePopupContent(props: { name: string; statusLabel: string }) {
  return (
    <View style={popupStyles.container}>
      <View style={popupStyles.title}>
        <AHIcon type="store" color="text-washed" forceSize={14} />
        <AHText type="subtitle" numberOfLines={1}>
          {props.name}
        </AHText>
      </View>
      <AHText type="info-small" color="text-washed">
        {props.statusLabel}
      </AHText>
    </View>
  );
}

const popupStyles = StyleSheet.create({
  container: {
    gap: 2,
  },
  title: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
});

export function OrgStoresMap(props: { stores: OrgStoresMap_stores$key }) {
  const stores = useFragment(
    graphql`
      fragment OrgStoresMap_stores on Store @relay(plural: true) {
        id
        name
        latitude
        longitude
        lastActiveAt
      }
    `,
    props.stores
  );

  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupContainerRef = useRef<HTMLDivElement | null>(null);
  const layout = useLayoutContext();
  const theme = useTheme();
  const storesRef = useRef(stores);
  storesRef.current = stores;

  const [popupContent, setPopupContent] = useState<PopupContent | null>(null);

  if (!popupContainerRef.current) {
    popupContainerRef.current = document.createElement("div");
  }

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const initialBounds = computeBounds(storesRef.current);

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      ...(initialBounds
        ? {
            bounds: initialBounds,
            fitBoundsOptions: { padding: FIT_PADDING, maxZoom: 12 },
          }
        : { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM }),
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const { activeNowCutoff, recentCutoff } = useMemo(() => {
    const now = Date.now();
    return {
      activeNowCutoff: now - 4 * 60 * 60 * 1000,
      recentCutoff: now - 4 * 24 * 60 * 60 * 1000,
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const popupContainer = popupContainerRef.current;
    if (!map || !popupContainer) return;

    const activeColor = theme["status-active"];
    const inactiveColor = theme["text-washed"];

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: MARKER_SIZE / 2 + 4,
      className: "store-marker-popup",
    });
    const addMarkers = () => {
      for (const marker of markersRef.current) {
        marker.remove();
      }
      markersRef.current = [];

      for (const store of stores) {
        if (store.latitude == null || store.longitude == null) continue;

        const lastActive = store.lastActiveAt
          ? new Date(store.lastActiveAt).getTime()
          : null;
        const isActiveNow = lastActive != null && lastActive >= activeNowCutoff;
        const isRecent = lastActive != null && lastActive >= recentCutoff;
        const color = isRecent ? activeColor : inactiveColor;
        const el = createMarkerElement(color, isActiveNow);

        const statusLabel = isActiveNow
          ? intl.formatMessage({
              defaultMessage: "Active now",
              description: "OrgStoresMap tooltip status for store active now",
            })
          : isRecent
            ? intl.formatMessage({
                defaultMessage: "Active recently",
                description:
                  "OrgStoresMap tooltip status for store active in last 4 days",
              })
            : intl.formatMessage({
                defaultMessage: "Inactive",
                description:
                  "OrgStoresMap tooltip status for store with no recent activity",
              });

        const lngLat: [number, number] = [store.longitude, store.latitude];

        el.addEventListener("mouseenter", () => {
          setPopupContent({ name: store.name, statusLabel });
          popup.setLngLat(lngLat).setDOMContent(popupContainer).addTo(map);
        });

        el.addEventListener("mouseleave", () => {
          popup.remove();
          setPopupContent(null);
        });

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat(lngLat)
          .addTo(map);

        markersRef.current.push(marker);
      }
    };

    if (map.loaded()) {
      addMarkers();
    } else {
      map.once("load", addMarkers);
    }

    return () => {
      map.off("load", addMarkers);
      popup.remove();
    };
  }, [stores, theme, intl, activeNowCutoff, recentCutoff]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          flex: 1,
          minHeight: MIN_MAP_HEIGHT,
          borderRadius: layout.radius.large,
          overflow: "hidden",
        }}
      />
      {popupContent &&
        popupContainerRef.current &&
        createPortal(
          <StorePopupContent
            name={popupContent.name}
            statusLabel={popupContent.statusLabel}
          />,
          popupContainerRef.current
        )}
    </>
  );
}
